// Generator for Product Stats demo data
// Shapes are tailored to what ProductStats.tsx expects at render time

export type AggregationType = 'by_item' | 'by_user' | 'by_pedido_type' | 'by_menu_category';
export type TimeRange = 'current' | '7d' | '30d' | 'custom';

// Utilities
const rand = (min: number, max: number) => Math.random() * (max - min) + min;
const randint = (min: number, max: number) => Math.floor(rand(min, max + 1));

// Simple normal distribution (Box-Muller)
const randn = () => {
  let u = 0, v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
};
const normal = (mean: number, sd: number) => Math.max(0, mean + sd * randn());
const round100 = (n: number) => Math.round(n / 100) * 100;

// Catalogs (intentionally different from any prior demo data)
const categories = [
  { id: 101, name: 'Pizzas de autor', price: [13900, 18900], items: ['Diavola', 'Capricciosa', 'Bianca Trufa', 'Napoletana', 'Quattro Stagioni', 'Prosciutto Crudo', 'Funghi Porcini', 'Calabrese'] },
  { id: 102, name: 'Pastas Frescas', price: [13900, 17900], items: ['Tagliatelle al Ragù', 'Rigatoni alla Vodka', 'Paccheri al Pomodoro', 'Orecchiette al Pesto', 'Lasagna Boloñesa', 'Cavatelli Funghi'] },
  { id: 103, name: 'Antipasti', price: [6900, 11900], items: ['Bruschette Misti', 'Focaccia al Romero', 'Carpaccio di Manzo', 'Burrata al Pesto'] },
  { id: 104, name: 'Ensaladas', price: [10900, 14900], items: ['Tricolore', 'Rúcula y Parmigiano', 'Insalata Toscana', 'Misticanza'] },
  { id: 105, name: 'Panadería', price: [2500, 4500], items: ['Focaccia', 'Grissini', 'Pan de Ajo', 'Ciabatta'] },
  { id: 106, name: 'Bebidas Italianas', price: [3200, 4200], items: ['San Pellegrino', 'Aranciata', 'Chinotto', 'Acqua Panna'] },
  { id: 107, name: 'Cafetería', price: [2500, 4200], items: ['Espresso Doble', 'Macchiato', 'Flat White', 'Mocha', 'Affogato'] },
  { id: 108, name: 'Cervezas', price: [3900, 5200], items: ['Peroni', 'Moretti', 'Heineken', 'Kross'] },
  { id: 109, name: 'Vinos por copa', price: [4500, 6900], items: ['Carmenere Reserva', 'Sauvignon Blanc', 'Merlot', 'Pinot Noir'] },
  { id: 110, name: 'Postres', price: [5500, 7900], items: ['Cannoli', 'Gelato artesano', 'Tiramisú de la casa', 'Zabaglione'] },
  // NEW: Granel (weight/volume) categories
  { id: 111, name: 'Quesos Artesanales', price: [18000, 35000], items: ['Queso Fresco de Cabra', 'Parmigiano Reggiano', 'Manchego Curado', 'Gorgonzola DOP'], isGranel: true },
  { id: 112, name: 'Aceites Premium', price: [12000, 28000], items: ['Aceite de Oliva Virgen Extra', 'Aceite de Trufa Negra', 'Aceite de Hierbas'], isGranel: true },
  { id: 113, name: 'Condimentos a Granel', price: [8000, 15000], items: ['Sal Marina con Hierbas', 'Pimienta Negra Molida', 'Orégano Fresco'], isGranel: true },
];

const users = [
  { id: 201, username: 'Martín' },
  { id: 202, username: 'Camila' },
  { id: 203, username: 'Sofía' },
  { id: 204, username: 'Benjamín' },
  { id: 205, username: 'Antonia' },
  { id: 206, username: 'Valentina' },
  { id: 207, username: 'Javiera' },
  { id: 208, username: 'Diego' },
];

const selectionGroups = [
  { id: 11, name: 'Salsas artesanales', items: ['Arrabbiata', 'Pomodoro rústico', 'Pesto genovese', 'Gorgonzola'] },
  { id: 12, name: 'Favoritos de la casa', items: ['Bosca', 'Capriccio', 'Rustica', 'Trufa Negra'] },
];

const modifiers = [
  { id: 11, name: 'Doble Queso' },
  { id: 12, name: 'Masa integral' },
  { id: 13, name: 'Sin Ajo' },
  { id: 14, name: 'Picante +' },
  { id: 15, name: 'Extra Olivas' },
];

function timeFactor(range: TimeRange): number {
  switch (range) {
    case 'current': return 1;
    case '7d': return 7;
    case '30d': return 30;
    case 'custom': return 7; // Default to 7 for custom (user should see real data from API anyway)
  }
}

export function generateProductStatsFakeData(aggregationType: AggregationType, range: TimeRange = 'current') {
  const factor = timeFactor(range);

  if (aggregationType === 'by_item') {
    // Build items with extra randomness
    const items: Record<string, any> = {};
    let autoId = 500;

    categories.forEach(cat => {
      const catPopularity = Math.min(1.6, Math.max(0.5, normal(1, 0.25))); // 0.5x–1.6x
      const itemCount = randint(3, Math.min(7, cat.items.length));
      const chosen = [...cat.items].sort(() => 0.5 - Math.random()).slice(0, itemCount);
      chosen.forEach(name => {
        const item_id = autoId++;
        const baseUnits = normal(16, 7) * catPopularity; // baseline units per day
        const itemSkew = 0.75 + Math.random() * 0.9; // 0.75–1.65
        const qty = Math.max(1, Math.round(baseUnits * itemSkew * factor));
        const price = round100(rand(cat.price[0], cat.price[1]));
        
        // NEW: Determine if this is a granel item
        const isGranelItem = (cat as any).isGranel || Math.random() < 0.15; // 15% chance for regular categories to have granel items
        
        let total_quantity_sold = qty;
        let total_granel_amount = 0;
        let granel_orders_count = 0;
        let unidad_medida_id: number | null = null;
        let unidad_medida_breakdown: any = {};
        
        if (isGranelItem) {
          // For granel items, convert quantity to weight/volume
          total_quantity_sold = 0; // No unit count for pure granel items
          granel_orders_count = Math.max(1, Math.round(qty * 0.8)); // Slightly fewer orders than units
          
          // Assign random unit type
          const unitTypes = [1, 2, 3, 4]; // g, kg, ml, L
          unidad_medida_id = unitTypes[randint(0, unitTypes.length - 1)];
          
          // Generate granel amount based on unit type
          switch (unidad_medida_id) {
            case 1: // grams
              total_granel_amount = Math.round(rand(200, 2000) * granel_orders_count);
              break;
            case 2: // kilograms  
              total_granel_amount = Math.round(rand(0.5, 5) * granel_orders_count * 100) / 100;
              break;
            case 3: // milliliters
              total_granel_amount = Math.round(rand(100, 1500) * granel_orders_count);
              break;
            case 4: // liters
              total_granel_amount = Math.round(rand(0.25, 3) * granel_orders_count * 100) / 100;
              break;
          }
          
          // Sometimes add multiple units breakdown
          if (Math.random() < 0.3) {
            const secondUnit = unitTypes.filter(u => u !== unidad_medida_id)[randint(0, 2)];
            const primaryPortion = 0.6 + Math.random() * 0.3; // 60-90% primary unit
            const secondaryPortion = 1 - primaryPortion;
            
            unidad_medida_breakdown = {
              [unidad_medida_id]: {
                granel_amount: Math.round(total_granel_amount * primaryPortion * 100) / 100,
                orders_count: Math.max(1, Math.round(granel_orders_count * primaryPortion))
              },
              [secondUnit]: {
                granel_amount: Math.round(total_granel_amount * secondaryPortion * 100) / 100,
                orders_count: Math.max(1, Math.round(granel_orders_count * secondaryPortion))
              }
            };
          } else {
            unidad_medida_breakdown = {
              [unidad_medida_id]: {
                granel_amount: total_granel_amount,
                orders_count: granel_orders_count
              }
            };
          }
        }
        
        const total_monetary_value = round100(isGranelItem ? 
          (granel_orders_count * price * rand(0.8, 1.2)) : // Granel pricing variation
          (qty * price)
        );

        // Split by pedido type
        const rWeight = 0.55 + Math.random() * 0.3; // 55–85% restaurant
        const dWeight = 1 - rWeight;
        const by_pedido_type: Record<string, any> = {
          '1': { 
            name: 'Restaurant', 
            quantity: Math.round((isGranelItem ? 0 : qty) * rWeight), 
            monetary_value: round100(total_monetary_value * rWeight), 
            total_percentage: 0,
            ...(isGranelItem && {
              granel_amount: Math.round(total_granel_amount * rWeight * 100) / 100,
              granel_orders_count: Math.max(1, Math.round(granel_orders_count * rWeight))
            })
          },
          '3': { 
            name: 'Delivery', 
            quantity: Math.round((isGranelItem ? 0 : qty) * dWeight), 
            monetary_value: round100(total_monetary_value * dWeight), 
            total_percentage: 0,
            ...(isGranelItem && {
              granel_amount: Math.round(total_granel_amount * dWeight * 100) / 100,
              granel_orders_count: Math.max(1, Math.round(granel_orders_count * dWeight))
            })
          },
        };

        // Split by users (2–5 randomly)
        const servingUsers = [...users].sort(() => 0.5 - Math.random()).slice(0, randint(2, 5));
        const weights = servingUsers.map(() => Math.random());
        const wsum = weights.reduce((a, b) => a + b, 0) || 1;
        const by_user: Record<string, any> = {};
        servingUsers.forEach((u, i) => {
          const portion = weights[i] / wsum;
          by_user[String(u.id)] = {
            username: u.username,
            quantity: Math.max(isGranelItem ? 0 : 1, Math.round((isGranelItem ? 0 : qty) * portion)),
            monetary_value: round100(total_monetary_value * portion),
            total_percentage: 0,
            ...(isGranelItem && {
              granel_amount: Math.round(total_granel_amount * portion * 100) / 100,
              granel_orders_count: Math.max(1, Math.round(granel_orders_count * portion))
            })
          };
        });

        // Accompaniments: 0–2 selection groups and 0–2 modifiers with random counts
        const accompanied_with: any = { selection_groups: {}, modifiers: {} };
        const sgCount = randint(0, 2);
        const chosenGroups = [...selectionGroups].sort(() => 0.5 - Math.random()).slice(0, sgCount);
        chosenGroups.forEach(g => {
          accompanied_with.selection_groups[String(g.id)] = {};
          const itemPicks = randint(1, Math.min(3, g.items.length));
          g.items.sort(() => 0.5 - Math.random()).slice(0, itemPicks).forEach((sgItem, idx) => {
            accompanied_with.selection_groups[String(g.id)][String(idx + 1)] = {
              item_name: sgItem,
              count: randint(1, Math.max(2, Math.round(qty * rand(0.05, 0.18))))
            };
          });
        });
        const modCount = randint(0, 2);
        [...modifiers].sort(() => 0.5 - Math.random()).slice(0, modCount).forEach((mod, i) => {
          accompanied_with.modifiers[String(mod.id)] = {
            [String(i + 1)]: { option_name: mod.name, count: randint(1, Math.max(2, Math.round(qty * rand(0.03, 0.1)))) }
          };
        });

        items[String(item_id)] = {
          item_name: name,
          category_id: cat.id,
          category_name: cat.name,
          total_quantity_sold: isGranelItem ? 0 : qty,
          total_monetary_value,
          by_pedido_type,
          by_user,
          by_third_party: {},
          accompanied_with,
          total_percentage: 0, // filled globally below
          // NEW: Granel fields
          ...(isGranelItem && {
            total_granel_amount,
            granel_orders_count,
            unidad_medida_id,
            unidad_medida_breakdown
          })
        };
      });
    });

    // Compute percentages
    const totalRevenue = Object.values(items).reduce((sum: number, x: any) => sum + x.total_monetary_value, 0) || 1;
    Object.values(items).forEach((x: any) => {
      x.total_percentage = Math.round((x.total_monetary_value / totalRevenue) * 10000) / 100; // 2 decimals
      const itemTotal = x.total_monetary_value || 1;
      Object.values(x.by_pedido_type).forEach((p: any) => {
        p.total_percentage = Math.round((p.monetary_value / itemTotal) * 10000) / 100;
      });
      Object.values(x.by_user).forEach((u: any) => {
        u.total_percentage = Math.round((u.monetary_value / itemTotal) * 10000) / 100;
      });
    });

    return items;
  }

  if (aggregationType === 'by_user') {
    const data: Record<string, any> = {};
    users.forEach(u => {
      const qty = Math.max(1, Math.round(normal(60, 25) * factor));
      const value = round100(qty * normal(12500, 2800));
      const hasGranel = Math.random() < 0.4; // 40% of users have granel orders
      
      data[String(u.id)] = { 
        username: u.username, 
        total_quantity_sold: qty, 
        total_monetary_value: value, 
        total_percentage: 0,
        ...(hasGranel && {
          total_granel_amount: Math.round(rand(2.5, 15) * factor * 100) / 100,
          granel_orders_count: Math.max(1, Math.round(rand(3, 12) * factor))
        })
      };
    });
    const total = Object.values(data).reduce((s: number, x: any) => s + x.total_monetary_value, 0) || 1;
    Object.values(data).forEach((x: any) => (x.total_percentage = Math.round((x.total_monetary_value / total) * 10000) / 100));
    return data;
  }

  if (aggregationType === 'by_pedido_type') {
    const rWeight = 0.55 + Math.random() * 0.3;
    const dWeight = 1 - rWeight;
    const baseValue = round100(normal(1_100_000, 350_000) * factor);
    const data: Record<string, any> = {
      '1': { name: 'Restaurant', total_quantity_sold: Math.round(baseValue / 12000), total_monetary_value: round100(baseValue * rWeight), total_percentage: 0 },
      '3': { name: 'Delivery', total_quantity_sold: Math.round(baseValue / 14000), total_monetary_value: round100(baseValue * dWeight), total_percentage: 0 },
    };
    const total = Object.values(data).reduce((s: number, x: any) => s + x.total_monetary_value, 0) || 1;
    Object.values(data).forEach((x: any) => (x.total_percentage = Math.round((x.total_monetary_value / total) * 10000) / 100));
    return data;
  }

  // by_menu_category
  const data: Record<string, any> = {};
  categories.forEach(cat => {
    const catPopularity = Math.min(1.6, Math.max(0.5, normal(1, 0.25)));
    const qty = Math.max(1, Math.round(normal(300, 140) * factor * catPopularity));
    const avgPrice = (cat.price[0] + cat.price[1]) / 2;
    const value = round100(qty * avgPrice * (0.85 + Math.random() * 0.25));
    data[String(cat.id)] = { category_name: cat.name, total_quantity_sold: qty, total_monetary_value: value, total_percentage: 0 };
  });
  const total = Object.values(data).reduce((s: number, x: any) => s + x.total_monetary_value, 0) || 1;
  Object.values(data).forEach((x: any) => (x.total_percentage = Math.round((x.total_monetary_value / total) * 10000) / 100));
  return data;
}

// Pre-generated large dataset for demos (30d across all aggregations)
export const productStatsDemoLarge: Record<AggregationType, any> = {
  by_item: generateProductStatsFakeData('by_item', '30d'),
  by_user: generateProductStatsFakeData('by_user', '30d'),
  by_pedido_type: generateProductStatsFakeData('by_pedido_type', '30d'),
  by_menu_category: generateProductStatsFakeData('by_menu_category', '30d'),
};
